import { IGame, IGameAttendances } from "@grassrootly/api";
import { PlayerGameAttendance } from "../../../screens/edit-game/useEditGame";
import { useEffect, useState } from "react";
import { MainStackParamList } from "../../../navigation/Navigation.types";
import { DrawerNavigationProp } from "@react-navigation/drawer";

export type EditGameAttendeesProps = {
  attendees: IGameAttendances[];
  setAttendance: (params: PlayerGameAttendance[]) => void;
  game: IGame;
  navigation: DrawerNavigationProp<MainStackParamList, "edit-game", undefined>;
};

export interface IEditGameAttendeesHook {
  filteredAttendees: IGameAttendances[];
  attendees: IGameAttendances[];
  updateAttendance: (attendee: IGameAttendances, attending: boolean) => void;
  updatePosition: (attendee: IGameAttendances, position: string | null) => void;
  addInTeam: (attendee: IGameAttendances, team: string) => void;
  isAttending: (attendee: IGameAttendances) => number;
  isInTeam: (attendee: IGameAttendances, team: string) => number;
  isGoalkeeper: (attendee: IGameAttendances) => boolean;
  unsavedChanges: IGameAttendances[];
  clearChanges: () => void;
  saveChanges: () => void;
  isUpdating: (playerId: number) => boolean;
}

export function useEditGameAttendees(props: EditGameAttendeesProps): IEditGameAttendeesHook {
  const { attendees, setAttendance } = props;
  const [filteredAttendees, setFilteredAttendees] = useState<IGameAttendances[]>([]);
  const [unsavedChanges, setUnsavedChanges] = useState<IGameAttendances[]>([]);
  const [updatingPlayers, setUpdatingPlayers] = useState<Set<number>>(new Set());

  useEffect(() => {
    clearChanges();
  }, [attendees]);

  const addInTeam = (attendee: IGameAttendances, team: string) => {
    const at = { ...attendee };
    at.team = team;
    updateLocalState(at);
  };

  const autoSaveAttendance = async (attendee: IGameAttendances) => {
    try {
      // Ensure loader is visible for at least 500ms
      const [, ] = await Promise.all([
        new Promise(resolve => setTimeout(resolve, 500)),
        (async () => {
          const params: PlayerGameAttendance[] = [{
            playerId: attendee.player_id,
            attendance: !!attendee.attended,
            team: attendee.team as string,
            position: attendee.position,
          }];
          await setAttendance(params);
          
          // Remove from unsaved changes since it's now saved
          setUnsavedChanges(prev => prev.filter(x => x.player_id !== attendee.player_id));
        })()
      ]);
    } finally {
      // Remove loading state for this player
      setUpdatingPlayers(prev => {
        const newSet = new Set(prev);
        newSet.delete(attendee.player_id);
        return newSet;
      });
    }
  };

  const updateAttendance = (attendee: IGameAttendances, attending: boolean) => {
    // Set loading state immediately before any processing
    setUpdatingPlayers(prev => new Set([...prev, attendee.player_id]));
    
    const at = { ...attendee };
    at.attended = !!attending;
    // For TRAINING games, don't modify team assignment in Attendees tab
    // Team assignment will be handled in the Lineups tab
    updateLocalState(at);
    
    // Auto-save this single change (don't await to avoid blocking UI)
    autoSaveAttendance(at);
  };

  const saveChanges = async () => {
    const params: PlayerGameAttendance[] = unsavedChanges.map((x) => ({
      playerId: x.player_id,
      attendance: !!x.attended,
      team: x.team as string,
      position: x.position,
    }));
    await setAttendance(params);
    setUnsavedChanges([]);
  };

  const clearChanges = () => {
    const a = [...attendees].sort((a, b) => {
      return a.player_id > b.player_id ? 1 : a.player_id < b.player_id ? -1 : 0;
    });
    setFilteredAttendees(a);
    setUnsavedChanges([]);
  };

  const updateLocalState = (attendee: IGameAttendances) => {
    // Update filtered attendees list
    const fa = [...filteredAttendees];
    const exIndex = fa.findIndex((x) => x.player_id === attendee.player_id);
    if (exIndex !== -1) {
      fa.splice(exIndex, 1);
    }
    fa.push({ ...attendee });
    sortAttendances(fa);
    setFilteredAttendees(fa);
    // Update unsaved changes
    const uc = [...unsavedChanges];
    const existsingIndex = uc.findIndex((x) => x.player_id === attendee.player_id);
    if (existsingIndex !== -1) {
      uc.splice(existsingIndex, 1);
    }
    uc.push({ ...attendee });
    setUnsavedChanges(uc);
  };

  const sortAttendances = (atts: IGameAttendances[]) => {
    atts.sort((a, b) => {
      return a.player_id > b.player_id ? 1 : a.player_id < b.player_id ? -1 : 0;
    });
  };

  const isAttending = (attendee: IGameAttendances): number => {
    return attendee?.attended == null ? -1 : attendee?.attended == false ? 0 : 1;
  };

  const isInTeam = (attendee: IGameAttendances, team: string): number => {
    return attendee?.team == null ? -1 : attendee?.team == team ? 1 : 0;
  };

  const isUpdating = (playerId: number): boolean => {
    return updatingPlayers.has(playerId);
  };

  const updatePosition = (attendee: IGameAttendances, position: string | null) => {
    // Set loading state immediately before any processing
    setUpdatingPlayers(prev => new Set([...prev, attendee.player_id]));
    
    const at = { ...attendee };
    at.position = position;
    updateLocalState(at);
    
    // Auto-save this single change
    autoSaveAttendance(at);
  };

  const isGoalkeeper = (attendee: IGameAttendances): boolean => {
    return attendee?.position === "GK";
  };

  return {
    filteredAttendees,
    attendees,
    unsavedChanges,
    updateAttendance,
    updatePosition,
    addInTeam,
    isAttending,
    isInTeam,
    isGoalkeeper,
    saveChanges,
    clearChanges,
    isUpdating,
  };
}
